import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Sparkles, Trophy, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";

const STORAGE_DISMISS = "summit:quest-dismissed";

/**
 * Onboarding Quest sidebar card. Auto-hides if all 6 actions are done AND
 * the badge has been claimed, OR if the user explicitly dismissed it.
 */
export default function OnboardingQuest({ onDone }) {
    const { user } = useAuth();
    const [quest, setQuest] = useState(null);
    const [dismissed, setDismissed] = useState(() => localStorage.getItem(STORAGE_DISMISS) === "1");
    const [claiming, setClaiming] = useState(false);

    const fetchQuest = useCallback(async () => {
        if (!user) return;
        try {
            const { data } = await api.get("/gamification/quest");
            setQuest(data);
            if (data.claimed) onDone?.();
        } catch { /* ignore */ }
    }, [user, onDone]);

    useEffect(() => { fetchQuest(); }, [fetchQuest]);

    if (!user) return null;
    if (dismissed) return null;
    if (!quest) return null;
    if (quest.claimed) return null;

    const claim = async () => {
        setClaiming(true);
        try {
            await api.post("/gamification/quest/claim");
            toast.success("First Step badge unlocked! +75 XP");
            await fetchQuest();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setClaiming(false);
        }
    };

    const dismiss = () => {
        localStorage.setItem(STORAGE_DISMISS, "1");
        setDismissed(true);
    };

    const pct = Math.round((quest.completed / quest.total) * 100);

    return (
        <section data-testid="onboarding-quest" className="relative bg-snowcap border-2 border-ember-orange/30 rounded-xl p-5">
            <button
                onClick={dismiss}
                aria-label="Dismiss"
                data-testid="quest-dismiss"
                className="absolute top-3 right-3 w-7 h-7 rounded-full hover:bg-trail-tan/40 flex items-center justify-center text-slate-gray"
            >
                <X className="w-4 h-4" />
            </button>
            <header className="flex items-center gap-2 mb-1">
                <Trophy className="w-4 h-4 text-ember-orange" />
                <h3 className="font-display font-bold text-base text-midnight-pine">First Step Quest</h3>
            </header>
            <p className="text-xs text-slate-gray mb-3">Six quick steps to unlock your first badge + 75 XP.</p>
            <div className="h-2 bg-trail-tan/60 rounded-full overflow-hidden mb-1">
                <div className="h-full bg-ember-orange transition-all" style={{ width: `${pct}%` }} />
            </div>
            <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-bold text-slate-gray mb-3">
                <span>{quest.completed} / {quest.total}</span>
                <span>{pct}%</span>
            </div>

            <ul className="space-y-1.5">
                {quest.steps.slice(0, 6).map((s) => (
                    <QuestRow key={s.id} step={s} />
                ))}
            </ul>

            {quest.ready_to_claim && (
                <button
                    onClick={claim}
                    disabled={claiming}
                    data-testid="quest-claim-btn"
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-ember-orange text-white text-sm font-bold hover:bg-ember-dark active:scale-95 transition-all disabled:opacity-50"
                >
                    <Sparkles className="w-4 h-4" /> {claiming ? "Unlocking…" : "Claim First Step badge + 75 XP"}
                </button>
            )}
        </section>
    );
}

function QuestRow({ step }) {
    const Icon = step.done ? CheckCircle2 : Circle;
    const progressText = step.progress && !step.done
        ? ` (${step.progress.current} / ${step.progress.target})`
        : "";
    return (
        <li data-testid={`quest-step-${step.id}`} className="flex items-start gap-2 text-sm">
            <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${step.done ? "text-moss" : "text-slate-gray"}`} />
            <div className="flex-1 min-w-0">
                <span className={step.done ? "line-through text-slate-gray" : "text-granite"}>
                    {step.title}{progressText}
                </span>
                {!step.done && step.cta && step.cta_label && (
                    <Link to={step.cta} className="ml-2 text-[11px] font-bold text-ember-orange hover:underline">
                        {step.cta_label} →
                    </Link>
                )}
            </div>
        </li>
    );
}
